/**
 * Utilities for iterators and iterables.
 *
 * Currently (as of Sep 2021) just async iterators, but functions like
 * tee() could be extended to work with either kind. That work should
 * be done as-needed.
 *
 * Before adding a new function/class to this file, check third-party
 * libraries we're already using, like Lodash.
 */
import { createPromise } from './promise';

/**
 * "AsyncIterableIteratorIterable": A slightly narrower type for async
 * iterables.
 */
type AIII<T> = {
  [Symbol.asyncIterator](): AsyncIterableIterator<T>;
};

export async function* mapAsync<T, U>(
  iter: AsyncIterable<T>,
  cb: (_: T) => U,
): AsyncIterableIterator<U> {
  for await (const x of iter) {
    yield cb(x);
  }
}

export function isAsyncIterable<T, U>(x: T | AsyncIterable<U>): x is AsyncIterable<U> {
  return typeof x === 'object' && !!x && Symbol.asyncIterator in x;
}

export type NestedAsyncIterable<T> = T | AsyncIterable<NestedAsyncIterable<T>>;
export async function* deepMapAsync<T, U>(
  iterOrItem: NestedAsyncIterable<T>,
  cb: (x: T) => U,
): NestedAsyncIterable<U> {
  if (!isAsyncIterable(iterOrItem)) {
    yield cb(iterOrItem);
    return;
  }
  yield* mapAsync(iterOrItem, child => deepMapAsync(child, cb));
}

/**
 * Recursively and concurrently walk through a NestedAsyncIterable,
 * pulling all of its values in the process.
 */
export async function concurrentWalk<T>(
  iterOrItem: NestedAsyncIterable<T>,
  cb: (x: T) => void,
): Promise<void> {
  if (!isAsyncIterable(iterOrItem)) {
    cb(iterOrItem);
    return;
  }
  for await (const child of iterOrItem) {
    // Adding `await` here would make this sequential, which we don't want.
    concurrentWalk(child, cb);
  }
}

export type WriterAndAsyncIterator<In, Out = In> = {
  iterator: AsyncIterableIterator<Out>;
  writer: {
    push: (x: In) => void;
    close: () => void;
  };
};

/**
 * asyncSource<T> returns a new READ-ONCE async iterator and a "writer"
 * to push values into it. Useful for converting callback-based code
 * into async iteration.
 *
 * If many push() calls will happen at once, consider using
 * batchedSource<T>() instead.
 */
export function asyncSource<T>(): WriterAndAsyncIterator<T> {
  const { iterator, writer } = batchedSource<T>();
  const flatIterator = (async function* () {
    for await (const batch of iterator) {
      yield* batch;
    }
  })();
  return {
    iterator: flatIterator,
    writer,
  };
}

/**
 * batchedSource<T> constructs a writable, read-once async iterator
 * like asyncSource(), but yields its values in batches accumulated
 * between next() calls. Array iteration is much faster than async
 * iteration, so this can be a performance boost.
 */
export function batchedSource<T>(): WriterAndAsyncIterator<T, T[]> {
  let closed = false;
  let backlog: T[] = [];
  let flush = createPromise<void>();
  const push = (x: T): void => {
    if (closed) {
      throw new Error('attempted to push to closed async iterator');
    }
    backlog.push(x);
    flush.resolve();
  };
  const close = (): void => {
    closed = true;
    flush.resolve();
  };
  const iterator = (async function* (): AsyncIterableIterator<T[]> {
    while (!closed) {
      await flush.promise;
      flush = createPromise<void>(); // eslint-disable-line require-atomic-updates
      if (backlog.length > 0) {
        const batch = backlog;
        backlog = [];
        yield batch;
      }
    }
  })();
  return {
    writer: { push, close },
    iterator,
  };
}

// Utility type for cached async streams. Used to cache async iteration
// by cachedIterable.
export type AsyncLinkedList<T> = Promise<AsyncListNode<T>>;
export type AsyncListNode<T> = null | {
  value: T;
  rest: AsyncLinkedList<T>;
};

/**
 * Convert an async iterator into a lazy async list.
 * Note, however, that the first iterator result is read eagerly.
 */
export async function asyncIterToList<T>(iter: AsyncIterator<T>): AsyncLinkedList<T> {
  const { value, done } = await iter.next();
  if (done) {
    return null;
  }
  let theRest: AsyncLinkedList<T> | undefined;
  return {
    value,
    get rest(): AsyncLinkedList<T> {
      if (!theRest) {
        theRest = asyncIterToList<T>(iter);
      }
      return theRest;
    },
  };
}

/**
 * cachedIterable<T> returns an iterable that caches the values in
 * `iter`.
 *
 * See the implementation of tee() for an example and performance note.
 *
 * Values are only added to the cache as they are read, so if `iter`
 * itself is read by another source it won't get cached.
 */
export function cachedIterable<T>(iter: AsyncIterator<T>): AIII<T> {
  let root: AsyncLinkedList<T> | undefined;
  return {
    /**
     * Performance note: An iterator in progress only references the part
     * of the cache it needs to continue, allowing `root` to be garbage
     * collected. See the implementation of tee() in this package for
     * an example of code that takes advantage of this.
     */
    async *[Symbol.asyncIterator](): AsyncIterableIterator<T> {
      // asyncIterToList() consumes the first value eagerly and the
      // rest lazily.
      //
      // We want the whole thing to be lazy,
      // so don't call it until now.
      if (root === undefined) {
        root = asyncIterToList(iter);
      }
      let p = root;
      while (true) {
        const node = await p;
        if (!node) {
          break;
        }
        p = node.rest;
        yield node.value;
      }
    },
  };
}

type SmallTupleOf<T, N> = 0 extends N
  ? []
  : N extends 1
  ? [T]
  : N extends 2
  ? [T, T]
  : N extends 3
  ? [T, T, T]
  : N extends 4
  ? [T, T, T, T]
  : N extends 5
  ? [T, T, T, T, T]
  : N extends 6
  ? [T, T, T, T, T, T]
  : N extends 7
  ? [T, T, T, T, T, T, T]
  : N extends 8
  ? [T, T, T, T, T, T, T, T]
  : N extends 9
  ? [T, T, T, T, T, T, T, T, T]
  : T[];
/**
 * tee(iter, n?) returns n read-once copies of iter, lazily consuming
 * it. Useful for feeding a stream into a fixed number of readers. Use
 * cachedIterable() instead if you need to re-read arbitrarily many
 * times.
 */
export function tee<T, N extends number = 2>(
  iter: AsyncIterableIterator<T>,
  n?: N,
): SmallTupleOf<AsyncIterableIterator<T>, N> {
  const times = n ?? (2 as N);
  const cached = cachedIterable(iter);
  const result: AsyncIterableIterator<T>[] = [];
  for (let i = 0; i < times; i++) {
    // Instead calling `result.push(cached)` would be semantically equivalent,
    // but would GC-pin the root of the cache. See the performance note
    // for cached's asyncIterator method.
    result.push(cached[Symbol.asyncIterator]());
  }
  return result as unknown as SmallTupleOf<AsyncIterableIterator<T>, N>;
}

export function first<T>(iter: Iterable<T>): T | undefined {
  for (const elem of iter) {
    return elem;
  }
  return undefined;
}

export function* filter<T>(iter: Iterable<T>, condition: (elem: T) => boolean): Iterable<T> {
  for (const elem of iter) {
    if (condition(elem)) {
      yield elem;
    }
  }
}

export function* map<T, Mapped>(
  iter: Iterable<T>,
  transform: (elem: T) => Mapped,
): IterableIterator<Mapped> {
  for (const elem of iter) {
    yield transform(elem);
  }
}

export const find = <T>(iter: Iterable<T>, condition: (elem: T) => boolean) =>
  first(filter<T>(iter, condition));

export const empty = {
  *[Symbol.iterator](): IterableIterator<any> {},
  async *[Symbol.asyncIterator](): AsyncIterableIterator<any> {},
};

export type PeekableAsyncIterator<T> = AsyncIterableIterator<T> & {
  peek(): Promise<IteratorResult<T>>;
};

/**
 * Return an IterableIterator through {@arg iterable}, but augmented
 * with a peek() method.
 *
 * peekableAsyncIterator() is idempotent -- safe to call repeatedly.
 */
export function peekableAsyncIterator<T>(
  iterable: AsyncIterable<T> & Partial<PeekableAsyncIterator<T>>,
): PeekableAsyncIterator<T> {
  // Idempotence: Don't wrap twice.
  if (
    typeof iterable.peek === 'function' &&
    Symbol.asyncIterator in iterable &&
    typeof iterable.next === 'function'
  ) {
    return iterable as PeekableAsyncIterator<T>;
  }
  let head: Promise<IteratorResult<T>> | undefined;
  const iterator = iterable[Symbol.asyncIterator]();
  return {
    async peek() {
      if (head === undefined) {
        head = iterator.next();
      }
      return head;
    },
    next() {
      if (head !== undefined) {
        const result = head;
        head = undefined;
        return result;
      }
      return iterator.next();
    },
    [Symbol.asyncIterator]() {
      return this;
    },
  };
}

type AsyncComparator<X, Y> = (x: Promise<X>, y: Promise<Y>) => Promise<number>;

/**
 * Zip two sorted iterables together so that they advance in sync.
 * Useful for combining time-varying values.
 *
 * The passed comparison function is asynchronous so that it can exit
 * early based on out-of-band information.
 */
export async function* zipSorted<X, Y>(
  compare: AsyncComparator<X, Y>,
  xsIn: AsyncIterable<X>,
  ysIn: AsyncIterable<Y>,
): AsyncIterableIterator<[X, Y]> {
  const xs = peekableAsyncIterator(xsIn as PeekableAsyncIterator<X>);
  const ys = peekableAsyncIterator(ysIn as PeekableAsyncIterator<Y>);
  let x = await xs.next();
  let y = await ys.next();
  if (x.done || y.done) {
    throw new Error('Both input streams must be non-empty.');
  }
  while (!x.done && !y.done) {
    yield [x.value, y.value];
    const nextX = xs.peek();
    const nextY = ys.peek();
    const cmp = await compareAsyncResults(compare, nextX, nextY);

    // Note that both streams advance simultaneously when their values
    // align (cmp === 0).
    if (cmp <= 0) {
      x = await xs.next();
    }
    if (cmp >= 0) {
      y = await ys.next();
    }
  }
}

async function compareAsyncResults<X, Y>(
  compareValues: AsyncComparator<X, Y>,
  xr: Promise<IteratorResult<X>>,
  yr: Promise<IteratorResult<Y>>,
): Promise<number> {
  // This function uses Promise.race for control flow. This never-resolving
  // is used to defer to other alternatives.
  const never = new Promise<never>(() => {});
  const value = <T>(r: IteratorResult<T>) => (!r.done ? r.value : never);
  const xValue = xr.then(value);
  const yValue = yr.then(value);
  // Note that if both streams are closed, we don't care what we return.
  return Promise.race([
    compareValues(xValue, yValue),
    xr.then(r => (r.done ? 1 : never)),
    yr.then(r => (r.done ? -1 : never)),
  ]);
}
